import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import MonthlyTotals from "./MonthlyTotals";
import { permissionsFor, canDeleteTxn } from "../lib/txnPermissions";

const fmtMoney = (v) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(v ?? 0);

const fmtDate = (iso) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  }) : "—";

export default function TransactionModal({ txn, profile, calSeq, fyRange, onClose, onSaved }) {
  const [refs, setRefs] = useState(null);
  const [form, setForm] = useState({
    txn_inv:  txn.txn_inv  || "",
    txn_amt:  txn.txn_amt != null ? Number(txn.txn_amt).toFixed(2) : "",
    txn_chk:  txn.txn_chk  || "",
    txn_note: txn.txn_note || "",
    req_init: txn.req_init || "",
    pye_id:   txn.pye_id   || "",
    codes_id: txn.codes_id || "",
    pay_tp:   String(txn.pay_tp ?? "0"),
    pay_dt:   txn.pay_dt   || "",
    post_dt:  txn.post_dt  || "",
  });
  const [busy, setBusy]   = useState(false);
  const [err,  setErr]    = useState("");

  useEffect(() => {
    let ignore = false;
    api.refs().then((r) => { if (!ignore) setRefs(r); }).catch(() => {});
    return () => { ignore = true; };
  }, []);

  // Memoise permissions so `editable` / `allowedPayTps` are referentially stable
  // between renders as long as the underlying txn + profile don't change.
  const { editable, allowedPayTps } = useMemo(
    () => permissionsFor(txn, profile),
    [txn, profile]
  );
  const canEdit   = editable.size > 0;
  const canDelete = canDeleteTxn(txn, profile);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isFieldEditable = (k) => editable.has(k);

  // Memoise pay_tp option list so it isn't re-filtered on every keystroke
  const payTypeOptions = useMemo(() => {
    if (!refs?.payments) return [];
    const restrict = editable.has("pay_tp") && allowedPayTps !== null;
    return refs.payments.filter((p) => {
      if (!restrict) return true;
      return allowedPayTps.has(Number(p.id))
          || Number(p.id) === Number(form.pay_tp);
    });
  }, [refs, editable, allowedPayTps, form.pay_tp]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      // Build patch with only fields that (a) the user is allowed to change AND
      // (b) the user actually changed
      const patch = {};
      const fields = [
        ["txn_inv",  form.txn_inv,  txn.txn_inv  || ""],
        ["txn_amt",  Number(form.txn_amt), Number(txn.txn_amt ?? 0)],
        ["txn_chk",  form.txn_chk,  txn.txn_chk  || ""],
        ["txn_note", form.txn_note, txn.txn_note || ""],
        ["req_init", form.req_init, txn.req_init || ""],
        ["pye_id",   form.pye_id,   txn.pye_id   || ""],
        ["codes_id", form.codes_id, txn.codes_id || ""],
        ["pay_tp",   Number(form.pay_tp), Number(txn.pay_tp ?? 0)],
        ["pay_dt",   form.pay_dt,   txn.pay_dt   || ""],
        ["post_dt",  form.post_dt,  txn.post_dt  || ""],
      ];
      for (const [k, cur, prev] of fields) {
        if (!isFieldEditable(k)) continue;
        if (cur !== prev) patch[k] = cur === "" ? null : cur;
      }
      if (!Object.keys(patch).length) { onClose(); return; }
      await api.updateTransaction(txn.txn_seq, patch);
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    if (!window.confirm(`Delete transaction ${txn.txn_inv || "#" + txn.txn_seq}?`)) return;
    setBusy(true);
    try {
      await api.deleteTransaction(txn.txn_seq);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card txn-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720 }}
        data-testid="txn-modal"
      >
        <div className="modal-head">
          <h2>Transaction Detail</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="tm-close">×</button>
        </div>
        {!refs ? (
          <div className="loading-row" style={{ padding: 30 }}>
            <span className="spinner" /> Loading…
          </div>
        ) : (
          <form className="modal-form" onSubmit={submit}>
            <div className="form-grid">
              {/* Row 1: Request date + Requestor + Payee */}
              <div className="form-field">
                <label>Request date</label>
                <input
                  data-testid="tm-postdt"
                  type="date"
                  value={form.post_dt}
                  disabled={!isFieldEditable("post_dt")}
                  onChange={(e) => set("post_dt", e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Requestor</label>
                <select
                  data-testid="tm-req"
                  value={form.req_init}
                  disabled={!isFieldEditable("req_init")}
                  onChange={(e) => set("req_init", e.target.value)}
                >
                  <option value="">—</option>
                  {refs.requestors.map((r) => (
                    <option key={r.id} value={r.id}>{r.label} ({r.id})</option>
                  ))}
                </select>
              </div>
              <div className="form-field" style={{ gridColumn: "span 2" }}>
                <label>Payee</label>
                <select
                  data-testid="tm-pye"
                  value={form.pye_id}
                  disabled={!isFieldEditable("pye_id")}
                  onChange={(e) => set("pye_id", e.target.value)}
                >
                  <option value="">—</option>
                  {refs.payees.map((p) => (
                    <option key={p.id} value={p.id}>{p.id} · {p.label}</option>
                  ))}
                </select>
              </div>

              {/* Row 2: Code + Invoice + Amount */}
              <div className="form-field">
                <label>Code</label>
                <select
                  data-testid="tm-code"
                  value={form.codes_id}
                  disabled={!isFieldEditable("codes_id")}
                  onChange={(e) => set("codes_id", e.target.value)}
                >
                  <option value="">—</option>
                  {refs.codes.map((c) => (
                    <option key={c.id} value={c.id}>{c.id} · {c.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field" style={{ gridColumn: "span 2" }}>
                <label>Invoice {isFieldEditable("txn_inv") && <span className="req">*</span>}</label>
                <input
                  data-testid="tm-inv"
                  value={form.txn_inv}
                  maxLength={20}
                  disabled={!isFieldEditable("txn_inv")}
                  onChange={(e) => set("txn_inv", e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Amount (USD)</label>
                <input
                  data-testid="tm-amt"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.txn_amt}
                  disabled={!isFieldEditable("txn_amt")}
                  onChange={(e) => set("txn_amt", e.target.value)}
                  onBlur={(e) => {
                    const n = Number(e.target.value);
                    if (!Number.isNaN(n) && e.target.value !== "")
                      set("txn_amt", n.toFixed(2));
                  }}
                />
              </div>

              {/* Row 3: Pay type + Check # + Pay date */}
              <div className="form-field">
                <label>Pay type</label>
                <select
                  data-testid="tm-pay"
                  value={form.pay_tp}
                  disabled={!isFieldEditable("pay_tp")}
                  onChange={(e) => set("pay_tp", e.target.value)}
                >
                  {payTypeOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.id} · {p.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Check #</label>
                <input
                  data-testid="tm-chk"
                  value={form.txn_chk}
                  maxLength={5}
                  disabled={!isFieldEditable("txn_chk")}
                  onChange={(e) => set("txn_chk", e.target.value)}
                />
              </div>
              <div className="form-field" style={{ gridColumn: "span 2" }}>
                <label>Pay date</label>
                <input
                  data-testid="tm-paydt"
                  type="date"
                  value={form.pay_dt}
                  disabled={!isFieldEditable("pay_dt")}
                  onChange={(e) => set("pay_dt", e.target.value)}
                />
              </div>

              {/* Row 4: Notes (full width) */}
              <div className="form-field form-field-wide">
                <label>Notes</label>
                <textarea
                  data-testid="tm-note"
                  className="notes-textarea"
                  rows={2}
                  maxLength={50}
                  value={form.txn_note}
                  disabled={!isFieldEditable("txn_note")}
                  onChange={(e) => set("txn_note", e.target.value)}
                />
              </div>
            </div>

            {err && <div className="form-error" data-testid="tm-error">{err}</div>}

            <div className="modal-actions" style={{ justifyContent: "space-between" }}>
              <div>
                {canDelete && (
                  <button
                    type="button"
                    className="btn-danger"
                    data-testid="tm-delete"
                    onClick={handleDelete}
                    disabled={busy}
                  >Delete</button>
                )}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="submit"
                  className={`btn-primary ${canEdit ? "" : "btn-disabled"}`}
                  data-testid="tm-submit"
                  disabled={busy || !canEdit}
                  style={{ width: "auto", padding: "10px 22px", margin: 0 }}
                  title={canEdit ? "" : "You don't have permission to save changes on this row"}
                >
                  {busy ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
            {!canEdit && (
              <div className="muted" style={{ marginTop: 8, fontSize: 12 }} data-testid="tm-readonly-hint">
                You can view this transaction but don&apos;t have permission to change it.
              </div>
            )}
            {fyRange && calSeq && (
              <div className="td-monthly" style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
                <MonthlyTotals calSeq={calSeq} txn={txn} fyRange={fyRange} />
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

export { fmtMoney, fmtDate };
